import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./style.module.scss";
import coachnow_logo from "app/images/Logo_dark.svg";
import PriceCard from "./_components/PriceCard";
import BillingInformation from "./_components/BillingInformation";
import CheckoutLegalLinks from "app/components/CheckoutLegalLinks";
import price_tag from "app/images/green_price_tag.svg";
import onboarding from "app/images/academy-onboarding.png";
import { Toast } from "app/components/helpers";
import { Link, Redirect, useHistory, useLocation } from "react-router-dom";
import { cls } from "app/utils";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import { Authenticated, Loading } from "app/components/Wrappers";
import PricingToggle, { AcademyPlanCode } from "./_components/PricingToggle";
import { useTranslation } from "react-i18next";
import { AuthService } from "app/services";
import { formatPrice, parseCurrency } from "app/utils/currency";
import { usePromoOffer } from "app/hooks/usePromoOffer";
import PromoResolveError from "app/components/PromoResolveError";
import { hasCanceledStorePlan, repriceTargets } from "app/utils/subscription";
import {
  mergePaywallContent,
  paywallBillingTerm,
  usePaywallFlag,
} from "app/hooks/usePaywallFlag";

const ACADEMY_PLAN_CODES: AcademyPlanCode[] = ["coachacademy+yearly", "coachacademy+monthly"];

// Module-level so its identity is stable across renders; defining it inside
// the component would remount the whole subtree (incl. the Stripe form) on
// every re-render.
const CoachContent = ({
  userIsCoach,
  children,
}: {
  userIsCoach: boolean;
  children: React.ReactNode;
}) => {
  if (!userIsCoach) {
    return <Redirect to="/" />;
  }
  return <>{children}</>;
};

// `purchase` renders the direct-buy variant (/purchase/academy): the checkout
// charges immediately instead of starting a trial, with matching copy.
const AcademyCheckout = ({ purchase = false }: { purchase?: boolean }) => {
  const Auth: any = AuthService;
  const { t } = useTranslation();
  const { self } = useSelector((state: RootState) => state.user);
  const [toastAlert, setToastAlert] = useState(null);
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const linkedPromo = params.get("promo") || undefined;
  // The code dropped for locking onto a term this page may not move to (see
  // below); a different code arriving on the same route is resolved as usual.
  const [droppedPromo, setDroppedPromo] = useState<string>();
  const promo = linkedPromo === droppedPromo ? undefined : linkedPromo;
  // A direct purchase by a user who already pays Stripe for a plan re-prices
  // that subscription in place (plan.upgradeTargets) instead of buying anew:
  // an academy over PRO, Plus or Analyze, or its other term. The page then sells only the terms the
  // subscription may move to.
  const repriceTerms = purchase ? (repriceTargets(self, ACADEMY_PLAN_CODES) as AcademyPlanCode[]) : [];
  const reprice = repriceTerms.length > 0;
  // A re-price is billed in the subscription's own currency, whatever the link
  // says: the API re-prices in it, so the price and the code are previewed in it.
  const currency = parseCurrency(reprice ? self.plan.currency : params.get("currency"));
  const userIsCoach = self?.kind?.toLowerCase() === "coach";
  const hasActiveTrial = self?.subscription?.trialEndsAt >= new Date();
  // A plan still renewing in a store keeps the page closed until it is
  // cancelled there; one already cancelled does not stand in the way of a
  // Stripe plan for the time it has left. A trial never follows a live plan.
  const canceledStorePlan = purchase && hasCanceledStorePlan(self);
  const sendHome =
    !reprice &&
    !canceledStorePlan &&
    (hasActiveTrial ||
      self?.subscription?.status === "active" ||
      self?.subscription?.status === "trial" ||
      self?.hasActiveSubscription ||
      self?.hasTrialSubscription);
  // The trial paywall's copy can come from its GrowthBook flag, which is read
  // only once the paywall is on screen.
  const { flag: paywallFlag, ready: contentReady } = usePaywallFlag(
    "paywall-academy",
    {
      shown: Boolean(self) && !purchase && userIsCoach && !sendHome,
      user: self,
    },
  );
  // The term picked on the toggle; until then, the one the flag opens on.
  const [pickedPlan, setPlanCode] = useState<AcademyPlanCode>();
  const planCode: AcademyPlanCode =
    pickedPlan ??
    (paywallBillingTerm(paywallFlag) === "monthly"
      ? "coachacademy+monthly"
      : "coachacademy+yearly");

  // Resolve the plan's price + any offer for the current (code, plan, currency),
  // waiting for auth. The hook owns the resolve dispatch + freshness / loading /
  // error / retry (shared across the checkout routes).
  const { offer, baseAmount, loading, error: resolveError, lockedPlan, trialDays: promoTrialDays, resolveOffer } = usePromoOffer({
    code: promo,
    planCode,
    currency,
    ready: !!self,
  });
  const hasOffer = Boolean(offer);

  // The backend follows the term-lock: it returns the EFFECTIVE plan (discovered
  // code-only by the hook, since its planCode differs from the requested one), so
  // pin the page to it and hide the toggle when the code is term-locked.
  const lockedPlanCode = lockedPlan ? (lockedPlan as AcademyPlanCode) : undefined;
  // A term-locked code (tied to one billing term) resolves to that term's plan;
  // follow it so a locked link doesn't hang on the requested term's loader. A
  // lock onto a term this page may not move to (the plan the user is on) is
  // dropped with its code, as the API drops it: the page then prices the term
  // it can sell at the full price instead of waiting on a resolution the lock
  // never lets match.
  useEffect(() => {
    if (!lockedPlanCode) {
      return;
    }
    if (reprice && !repriceTerms.includes(lockedPlanCode)) {
      setDroppedPromo(linkedPromo);
    } else if (lockedPlanCode !== planCode) {
      setPlanCode(lockedPlanCode);
    }
    // planCode is a dep so a term toggle on a locked code re-asserts the lock
    // (snaps back) instead of leaving the page stuck on the mismatched term.
  }, [lockedPlanCode, planCode, reprice, repriceTerms]);
  useEffect(() => {
    if (reprice && !repriceTerms.includes(planCode)) {
      setPlanCode(repriceTerms[0]);
    }
  }, [reprice, repriceTerms, planCode]);

  // Backend-resolved (region-aware); 7 is a transient fallback shown only while
  // the resolve is in flight (the dynamic line is hidden during loading).
  const trialDays = promoTrialDays ?? 7;
  // Today's copy, which the flag's fields replace one by one.
  const content = mergePaywallContent(
    {
      headline: t("Start your Free {{trialDays}}-Day Trial", { trialDays }),
      subhead: t(
        "Your payment will process after the {{trialDays}}-day free trial period. Cancel at any time.",
        { trialDays },
      ),
      annual_badge: "-17%",
      plan_description: t(
        "For facilities and academies centralizing their coaching business — everything in PRO, plus scheduling, billing, and automation.",
      ),
      trial_headline: t("{{trialDays}} Days Free", { trialDays }),
      cta_text: t("Start Free Trial"),
      show_free_plan_button: true,
      free_plan_text: t("Continue with Free Plan"),
    },
    paywallFlag,
    trialDays,
  );
  // Phased two-step offer, straight from the resolved schedule (first phase amount
  // for 12 months, then the last phase amount). The 0 fallbacks never render —
  // these are only used when isPhased, i.e. when phases are present.
  const isPhased = Boolean(offer?.phased);
  const phases = offer?.phased?.phases ?? [];
  const phasedFirstAmount = phases[0]?.amount ?? 0;
  const phasedSecondAmount = phases.length
    ? phases[phases.length - 1].amount
    : 0;

  const onDone = () => {
    if (purchase) {
      // A direct purchase lands on the wizard immediately, the same way a
      // trial signup does — a fresh Academy account sets itself up there
      // first, with no success interstitial in between. The entry point stays
      // "upgrade" so the wizard analytics still tells the two apart.
      history.push("/academy/wizard", { wizardEntryPoint: "upgrade" });
    } else {
      setToastAlert(t("Your account was successfully updated."));
    }
  };

  const onPlanCodeChange = useCallback((code: AcademyPlanCode) => {
    setPlanCode(code);
  }, []);

  // Back returns into the signup funnel at the "How do you coach?" step, and
  // only exists for visitors who came from it (?from=coach-plan) — dedicated
  // marketing/invite links never saw the question, so they get no Back. The
  // rest of the query (promo, currency, sport...) rides along both ways so a
  // Back-then-reselect round trip keeps the visitor's discount context.
  const fromCoachPlan = params.get("from") === "coach-plan";
  const onBack = () => {
    const query = new URLSearchParams(location.search);
    query.delete("from");
    query.set("resume", "coach-plan");
    history.push(`/signup?${query.toString()}`);
  };

  // Description shown under the "Special Offer Applied!" badge. Reads e.g.
  // "60 days free, then $200 off for the first 12 months" — the trial prefix
  // only appears when the promo extends the trial past the standard 7 days.
  const couponDescription = useMemo(() => {
    const promoDiscount = offer?.discount;
    let discount: string | null = null;
    if (isPhased) {
      discount = t("{{first}} per month for 12 months, then {{second}} per month", {
        first: formatPrice(phasedFirstAmount, currency),
        second: formatPrice(phasedSecondAmount, currency),
      });
      // ACADEMYGGNASDY's "$200 off for the first 12 months" flows through the
      // amountOff+repeating branch below (offer.discount = amountOff 20000,
      // duration repeating, durationInMonths 12) — same copy, no special case.
    } else if (promoDiscount?.percentOff) {
      if (promoDiscount.duration === "forever") {
        discount = t("{{percent}}% off for life", { percent: promoDiscount.percentOff });
      } else if (promoDiscount.duration === "repeating" && promoDiscount.durationInMonths) {
        discount = t("{{percent}}% off for the first {{months}} months", {
          percent: promoDiscount.percentOff,
          months: promoDiscount.durationInMonths,
        });
      } else {
        discount = t("{{percent}}% off your first payment", {
          percent: promoDiscount.percentOff,
        });
      }
    } else if (promoDiscount?.amountOff) {
      const amount = formatPrice(promoDiscount.amountOff, currency);
      if (promoDiscount.duration === "forever") {
        discount = t("{{amount}} off for life", { amount });
      } else if (promoDiscount.duration === "repeating" && promoDiscount.durationInMonths) {
        discount = t("{{amount}} off for the first {{months}} months", {
          amount,
          months: promoDiscount.durationInMonths,
        });
      } else {
        discount = t("{{amount}} off your first payment", { amount });
      }
    }

    // A direct purchase never grants a trial, so only the discount matters.
    const showTrial = !purchase && trialDays > 7;
    if (showTrial && discount) {
      return t("{{trialDays}} days free, then {{discount}}", {
        trialDays,
        discount,
      });
    }
    if (showTrial) {
      return t("{{trialDays}} days free", { trialDays });
    }
    return discount;
  }, [offer, trialDays, isPhased, phasedFirstAmount, phasedSecondAmount, currency, purchase, t]);

  // A phased offer builds a subscription schedule, which only a purchase can;
  // on a re-price the API drops the code, so the page drops it too rather
  // than showing an intro price the update will not apply.
  useEffect(() => {
    if (reprice && isPhased) {
      setDroppedPromo(linkedPromo);
    }
  }, [reprice, isPhased, linkedPromo]);

  if (sendHome) {
    return <Redirect to="/" />;
  }

  // Also held while the flag loads, so the defaults don't flash before the
  // variant.
  if ((Auth.isAuthenticated && !self) || !contentReady) {
    return (
      <div className={styles.loading}>
        <Loading isLoading loadType="spinner" />
      </div>
    );
  }

  return (
    <Authenticated redirect="/signup">
      <CoachContent userIsCoach={userIsCoach}>
        <div
          className={cls(styles.container, "pro-trial-golf", {
            [styles.page_background]: location.pathname.includes("signup"),
          })}
        >
          <div className={styles.left_content}>
            <img
              className={styles.site_logo}
              src={coachnow_logo}
              alt="CoachNow Icon"
            />
            <div className={styles.content_container}>
              {fromCoachPlan && (
                <button
                  type="button"
                  className={styles.back}
                  onClick={onBack}
                  aria-label={t("Back")}
                >
                  <i className="fas fa-arrow-left" aria-hidden="true" />
                  {t("Back")}
                </button>
              )}
              <div className={styles.header}>
                <h3 className={styles.header_title}>
                  {reprice
                    ? t("Switch to CoachNow Academy")
                    : purchase
                    ? t("Start your CoachNow Academy subscription")
                    : loading
                    ? t("Start your Free Trial")
                    : content.headline}
                </h3>
                <p className={styles.header_description}>
                  {reprice
                    ? t("Your current plan's unused time is credited toward this payment. If the credit is larger than the payment, the rest stays on your account and covers your next bills.")
                    : purchase
                    ? t("To get started enter your card details")
                    : loading
                    ? t(
                        "Your payment will process after your free trial period. Cancel at any time.",
                      )
                    : content.subhead}
                </p>
              </div>
              {!loading && hasOffer && (
                <div className={styles.promo_applied_container}>
                  <div className={styles.promocode_applied}>
                    <img
                      src={price_tag}
                      alt="coupon tag"
                      className={styles.price_tag}
                    />
                    <div className={styles.promo_applied_wrapper}>
                      <p className={styles.promo_applied}>
                        {t("Special Offer Applied!")}
                      </p>
                      {couponDescription && (
                        <p className={styles.coupon_description}>
                          {couponDescription}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {/* A term-locked offer (offer.planLock, e.g. ACADEMYGGNASDY →
                  yearly) applies to a single plan, so the term selector is
                  hidden rather than shown with one option disabled. */}
              {!lockedPlanCode && (
                <PricingToggle
                  planCode={planCode}
                  onPlanSelect={onPlanCodeChange}
                  lockedPlan={lockedPlanCode}
                  // The static annual-savings badge yields to an applied promo,
                  // matching the pro/analyze pages.
                  discount={promo ? undefined : content.annual_badge || undefined}
                  availablePlans={reprice ? repriceTerms : undefined}
                />
              )}
              {resolveError ? (
                <PromoResolveError onRetry={resolveOffer} />
              ) : (
                <PriceCard
                  planCode={planCode}
                  currency={currency}
                  loading={loading || baseAmount == null}
                  isPhased={isPhased}
                  phasedFirstAmount={phasedFirstAmount}
                  phasedSecondAmount={phasedSecondAmount}
                  discount={offer?.discount}
                  baseAmount={baseAmount}
                  purchase={purchase}
                  description={content.plan_description}
                  trialHeadline={content.trial_headline}
                />
              )}
              {/* The plan switchers belong to the signup funnel (the coach
                  question): dedicated marketing/purchase links sell a single
                  plan and must not cross-sell away from it. */}
              {fromCoachPlan && (
                <div className={styles.cross_links}>
                  <p className={styles.cross_link_primary}>
                    <a
                      href="https://coachnow.com/organizations"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("Coaching with 3+ coaches?")}{" "}
                      {t("Get custom group pricing")} &rarr;
                    </a>
                  </p>
                  <p className={styles.cross_link}>
                    {/* Carry the whole query across: the promo/currency
                        context and the from-marker (so Back works there
                        too). */}
                    {/* Stay in the same mode: a direct buyer cross-links to
                        the pro purchase, a trial visitor to the pro trial. */}
                    <Link
                      to={{
                        pathname: purchase ? "/purchase/pro" : "/trial/pro",
                        search: location.search,
                      }}
                    >
                      {t("Just coaching on your own?")} {t("See PRO")} &rarr;
                    </Link>
                  </p>
                </div>
              )}
              <BillingInformation
                onDone={onDone}
                planCode={planCode}
                couponCode={promo}
                currency={currency}
                isPhased={isPhased}
                phasedSecondAmount={phasedSecondAmount}
                purchase={purchase}
                reprice={reprice}
                ctaText={content.cta_text}
                showFreePlanButton={content.show_free_plan_button}
                freePlanText={content.free_plan_text}
              />
              <CheckoutLegalLinks />
            </div>
            <Toast success={toastAlert} onDone={() => setToastAlert(null)} />
          </div>
          <div className={styles.right_content}>
            <img
              src={onboarding}
              alt="onboarding"
              className={styles.onboarding_img}
            />
          </div>
        </div>
      </CoachContent>
    </Authenticated>
  );
};

export default AcademyCheckout;
